import { GroundworksTableData, TableCellInput, TableGroup, TableRow } from '@groundworks/ngx-groundworks-ui';
import { BoundProductInfo, TaxInfo } from '../../sdk/sdk';
import { prorateAmount, roundMoney } from '../../utils/money';
import { unitAbbreviation } from '../../utils/unit-abbreviation';

/**
 * One product row of the split modal (MOD-3972): the source bound product's original
 * quantity/net next to the units/net the user is moving to the new project.
 */
export interface SplitLineView {
  boundProduct: BoundProductInfo;
  category: string;
  originalUnits: number;
  originalNet: number;
  moveUnits: number;
  moveNet: number;
}

/**
 * One job tax type's split between the two sides: the pool this split may draw from and how much
 * of it the user is allocating to the NEW project. The current side always shows the exact
 * remainder, so the pool is preserved by construction.
 *
 * `availableAmount` is the SERVER's per-type availability, NOT the job-level tax amount — the two
 * diverge once a split has partitioned the type, and capping on the job amount would let the modal
 * offer a ceiling Save & Complete rejects with a 400.
 */
export interface SplitTaxAllocationView {
  taxTypeId: string;
  label: string;
  availableAmount: number;
  allocated: number;
}

/** One rendered tax row of a totals group; `input` is present only on the new-project side. */
export interface SplitTaxLine {
  label: string;
  amount: number;
  input?: TableCellInput;
}

export interface SplitTableHandlers {
  onUnitsChange: (boundProductId: string, value: string | number) => void;
  onTotalChange: (boundProductId: string, value: string | number) => void;
  onTaxAllocationChange: (taxTypeId: string, value: string | number) => void;
}

const CATEGORY_ORDER = ['Installable Product', 'Non-Installable Product', 'Recurring Product'];
const FALLBACK_CATEGORY = 'Installable Product';

/** One (projectId, productId, note) group's real anchor row and its netted-and-capped totals. */
interface NettedBoundProductGroup {
  anchor: BoundProductInfo;
  originalUnits: number;
  originalNet: number;
}

/**
 * A descoped product leaves TWO raw rows behind: the original and a negative change-order
 * adjustment sitting alongside it (MOD-4576) — so netting groups by (projectId, productId, note),
 * exactly like `aggregateBoundProducts`, but never collapses the rows into a synthetic id: the
 * split submits real row ids, so each group's ANCHOR (the row without a `changeOrderId`, falling
 * back to the first row) is what the resulting line carries. The anchor's own value is the true
 * ceiling execution can decrement, so a group's totals are capped at `min(anchor's own value, net
 * across the group)` — the same ceiling the backend now validates against.
 */
function netBoundProductGroups(boundProducts: BoundProductInfo[]): NettedBoundProductGroup[] {
  const groups = new Map<string, BoundProductInfo[]>();
  for (const boundProduct of boundProducts) {
    const key = groupKey(boundProduct);
    const group = groups.get(key);
    if (group) group.push(boundProduct);
    else groups.set(key, [boundProduct]);
  }

  const result: NettedBoundProductGroup[] = [];
  for (const rows of groups.values()) {
    const netQuantity = rows.reduce((sum, r) => sum + (r.quantity ?? 0), 0);
    if (netQuantity <= 0) continue; // fully descoped (or a corrupt negative net) — nothing to show

    const netNet = rows.reduce((sum, r) => sum + (r.netAmount ?? 0), 0);
    const anchor = rows.find((r) => !r.changeOrderId) ?? rows[0];
    result.push({
      anchor,
      originalUnits: Math.max(0, Math.min(anchor.quantity ?? 0, netQuantity)),
      originalNet: Math.max(0, Math.min(anchor.netAmount ?? 0, netNet)),
    });
  }
  return result;
}

function groupKey(boundProduct: BoundProductInfo): string {
  const productId = boundProduct.productId ?? boundProduct.id;
  const note = boundProduct.note?.trim() ?? '';
  return `${boundProduct.projectId ?? ''}::${productId}::${note}`;
}

/**
 * Seeds one untouched line per netted product group, ordered by canonical category (Installable
 * → Non-Installable → Recurring, unknowns last) so the tables group cleanly.
 */
export function buildSplitLineViews(
  boundProducts: BoundProductInfo[],
  categoryByBoundProductId: ReadonlyMap<string, string>,
): SplitLineView[] {
  const categoryRank = (category: string) => {
    const rank = CATEGORY_ORDER.indexOf(category);
    return rank === -1 ? CATEGORY_ORDER.length : rank;
  };
  return netBoundProductGroups(boundProducts)
    .map(({ anchor, originalUnits, originalNet }) => ({
      boundProduct: anchor,
      category: categoryByBoundProductId.get(anchor.id) ?? FALLBACK_CATEGORY,
      originalUnits,
      originalNet,
      moveUnits: 0,
      moveNet: 0,
    }))
    .sort((a, b) => categoryRank(a.category) - categoryRank(b.category));
}

/**
 * The server's per-type allocation pool: the resolved map, `null` while the pre-flight is still in
 * flight, or `'unavailable'` once it has failed.
 */
export type SplitTaxAvailability = ReadonlyMap<string, number> | null | 'unavailable';

function resolveAvailablePool(tax: TaxInfo, availableByTaxType: SplitTaxAvailability): number {
  if (availableByTaxType === 'unavailable') return 0;
  if (availableByTaxType === null) return tax.amount;
  // A resolved pre-flight that omits a type means the server does not recognise it as one of the
  // job's taxes, so nothing may be allocated against it — 0, not the job amount.
  return availableByTaxType.get(tax.taxTypeId) ?? 0;
}

/**
 * One allocation view per job tax type. Types the user has not touched auto-follow the
 * proportional preview (rate × new-side subtotal, clamped to the available pool) so the
 * allocation tracks product moves until a manual entry pins it (map membership = touched).
 *
 * While the pre-flight is in flight the job-level amount stands in — identical to the pool on a
 * first split. Once it has FAILED the pool is unknown, so nothing may be allocated: the job amount
 * is right only on a first split, and auto-filling it otherwise produces a save the server rejects,
 * refusing the product moves along with it. Manual entries are re-clamped here too: availability
 * can land AFTER the user has typed.
 */
export function buildSplitTaxAllocations(
  taxes: TaxInfo[],
  newSubtotal: number,
  manualAllocations: ReadonlyMap<string, number>,
  availableByTaxType: SplitTaxAvailability,
): SplitTaxAllocationView[] {
  return taxes.map((tax) => {
    const available = resolveAvailablePool(tax, availableByTaxType);
    const preview = Math.min(Math.max(roundMoney(newSubtotal * tax.rate), 0), available);
    return {
      taxTypeId: tax.taxTypeId,
      label: `Taxes (${tax.taxTypeName ?? 'Tax'})`,
      availableAmount: available,
      allocated: Math.min(manualAllocations.get(tax.taxTypeId) ?? preview, available),
    };
  });
}

/** Read-only left table: the project's products at their original quantities and amounts. */
export function buildCurrentProjectTable(
  lines: SplitLineView[],
  taxAllocations: SplitTaxAllocationView[],
): GroundworksTableData {
  const subtotal = roundMoney(lines.reduce((sum, line) => sum + line.originalNet, 0));
  return {
    columns: buildColumns(),
    groups: [
      ...groupByCategory(lines, (line) => ({
        type: 'data' as const,
        cells: [
          { value: productName(line) },
          { value: `${line.originalUnits} ${line.boundProduct.productInfo?.unitOfMeasure ?? 'Each'}` },
          { value: unitPrice(line) },
          { value: line.originalNet },
        ],
      })),
      buildTotalsGroup(subtotal, buildCurrentTaxLines(taxAllocations), 'Contract Total'),
    ],
  };
}

/** Editable right table: per-row units + total inputs for the amounts moving to the new project. */
export function buildNewProjectTable(
  lines: SplitLineView[],
  taxAllocations: SplitTaxAllocationView[],
  handlers: SplitTableHandlers,
): GroundworksTableData {
  const subtotal = roundMoney(lines.reduce((sum, line) => sum + line.moveNet, 0));
  return {
    columns: buildColumns(),
    groups: [
      ...groupByCategory(lines, (line) => buildEditableRow(line, handlers)),
      buildTotalsGroup(subtotal, buildNewTaxLines(taxAllocations, handlers), 'New Project Total'),
    ],
  };
}

// The current side is always the exact remainder of the AVAILABLE pool — so the two sides still
// add up to what the split is actually dividing, which after a prior split is less than the
// job-level tax amount.
function buildCurrentTaxLines(taxAllocations: SplitTaxAllocationView[]): SplitTaxLine[] {
  return taxAllocations.map((tax) => ({
    label: tax.label,
    amount: roundMoney(tax.availableAmount - tax.allocated),
  }));
}

// The new side carries the editable allocation, mirroring the product rows' currency input.
function buildNewTaxLines(
  taxAllocations: SplitTaxAllocationView[],
  handlers: SplitTableHandlers,
): SplitTaxLine[] {
  return taxAllocations.map((tax) => ({
    label: tax.label,
    amount: tax.allocated,
    input: {
      type: 'currency',
      ariaLabel: `Tax to move for ${tax.label}`,
      defaultValue: tax.allocated > 0 ? tax.allocated.toFixed(2) : '',
      placeholder: '0.00',
      rules: { min: 0, max: tax.availableAmount, decimalPlaces: 2 },
      disabled: tax.availableAmount === 0,
      onChange: (value) => handlers.onTaxAllocationChange(tax.taxTypeId, value),
    },
  }));
}

function buildColumns(): GroundworksTableData['columns'] {
  return [
    { label: 'Item', dataType: 'text' },
    { label: 'Units', dataType: 'text', align: 'end' },
    { label: 'Price', dataType: 'currency' },
    { label: 'Total', dataType: 'currency' },
  ];
}

function buildEditableRow(line: SplitLineView, handlers: SplitTableHandlers): TableRow {
  const id = line.boundProduct.id;
  const name = productName(line);
  const immovable = line.originalUnits === 0;
  return {
    type: 'data',
    cells: [
      { value: name },
      {
        value: '',
        input: {
          type: 'number',
          suffix: unitAbbreviation(line.boundProduct.productInfo?.unitOfMeasure ?? 'Each'),
          ariaLabel: `Units to move for ${name}`,
          defaultValue: line.moveUnits > 0 ? line.moveUnits : '',
          placeholder: '0',
          rules: { min: 0, max: line.originalUnits, decimalPlaces: 0 },
          disabled: immovable,
          onChange: (value) => handlers.onUnitsChange(id, value),
        },
      },
      { value: unitPrice(line) },
      {
        value: '',
        input: {
          type: 'currency',
          ariaLabel: `Total to move for ${name}`,
          defaultValue: line.moveNet > 0 ? line.moveNet.toFixed(2) : '',
          placeholder: '0.00',
          rules: { min: 0, max: line.originalNet, decimalPlaces: 2 },
          disabled: immovable,
          onChange: (value) => handlers.onTotalChange(id, value),
        },
      },
    ],
  };
}

function groupByCategory(lines: SplitLineView[], toRow: (line: SplitLineView) => TableRow): TableGroup[] {
  const groups: TableGroup[] = [];
  for (const line of lines) {
    let group = groups.find((g) => g.label === line.category);
    if (!group) {
      group = { label: line.category, rows: [] };
      groups.push(group);
    }
    group.rows.push(toRow(line));
  }
  return groups;
}

function buildTotalsGroup(subtotal: number, taxLines: SplitTaxLine[], totalLabel: string): TableGroup {
  const grandTotal = roundMoney(subtotal + taxLines.reduce((sum, tax) => sum + tax.amount, 0));
  return {
    label: '',
    rows: [
      {
        type: 'subtotal',
        cells: [
          { value: 'Products Total', isHeader: true, colspan: 3, className: 'fw-bold bg-light' },
          { value: subtotal, className: 'fw-bold bg-light' },
        ],
      },
      ...taxLines.map((tax) => ({
        type: 'data' as const,
        cells: [
          { value: tax.label, colspan: 3 },
          tax.input ? { value: '', input: tax.input } : { value: tax.amount },
        ],
      })),
      {
        type: 'total',
        cells: [
          { value: totalLabel, isHeader: true, colspan: 3, className: 'fw-bold' },
          { value: grandTotal, className: 'fw-bold' },
        ],
      },
    ],
  };
}

function productName(line: SplitLineView): string {
  return line.boundProduct.productInfo?.name ?? 'Product';
}

function unitPrice(line: SplitLineView): number {
  return prorateAmount(line.originalNet, line.originalUnits, 1);
}
